// Detalle del viaje como hoja de ruta (PP-41): lo que el proveedor le pasa al
// chofer. Paradas en orden con hora planificada, pasajeros por parada (nombre
// y domicilio solo si la API los mando: viaje asignado a la empresa y
// vigente), vehiculo, conductor, planta, servicio y sentido. Nunca un id
// tecnico.
//
// "Imprimir" saca solo esta hoja (route-sheet.css) y "Compartir" arma el texto
// para WhatsApp con los mismos datos, del mismo modelo (logic/route-sheet.ts).
// "Copiar datos del viaje" (PP-65) arma, del mismo modelo, el texto para cargar
// el viaje en el sistema propio de la empresa.
//
// Se exporta para que la pantalla de Viajes (PP-62) la enchufe:
//   <TripRouteSheet session={...} port={requestsPort} site={siteAccess}
//     trip={trip} vehicles={vehicles} drivers={drivers} />
import "./route-sheet.css";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { Driver, ProviderTrip, ProviderTripDetailResponse, SiteVehicle } from "@ucorp/contracts";
import type { PortalSession } from "../auth/session.js";
import { Alert } from "../components/ui/alert.js";
import { Button } from "../components/ui/button.js";
import type { RosterState } from "../fleet/useRoster.js";
import { buildRouteSheet, dispatchText, passengerText, routeSheetText, stopHeading, type RouteSheet } from "../logic/route-sheet.js";
import type { RequestsPort, RequestsSite } from "../requests/port.js";
import { browserRouteSheetActions, type RouteSheetActions } from "./actions.js";
import { DISPATCH_COPIED } from "./DispatchCopy.js";

type ShareStatus = "idle" | "copied" | "dispatch_copied" | "shared" | "copy_failed";

const SHARE_COPY: Record<Exclude<ShareStatus, "idle">, { variant: "info" | "error"; text: string }> = {
  copied: { variant: "info", text: "Texto copiado. Pegalo en el chat con el conductor." },
  dispatch_copied: { variant: "info", text: DISPATCH_COPIED },
  shared: { variant: "info", text: "Hoja de ruta compartida." },
  copy_failed: { variant: "error", text: "No se pudo copiar el texto. Seleccionalo a mano desde la hoja." },
};

export interface RouteSheetViewProps {
  sheet: RouteSheet;
  /** Por defecto, las del navegador. */
  actions?: RouteSheetActions;
}

/** Hoja de ruta ya armada, con Imprimir y Compartir. Sin IO propio. */
export function RouteSheetView({ sheet, actions }: RouteSheetViewProps): React.JSX.Element {
  const browser = useMemo(() => actions ?? browserRouteSheetActions(), [actions]);
  const ref = useRef<HTMLElement>(null);
  const titleId = useId();
  const [status, setStatus] = useState<ShareStatus>("idle");
  const text = routeSheetText(sheet);
  const share = browser.share;

  const copy = (value: string, done: ShareStatus): void => {
    browser.copy(value).then(
      () => setStatus(done),
      () => setStatus("copy_failed"),
    );
  };

  return (
    <section ref={ref} aria-labelledby={titleId} className="flex flex-col gap-3 rounded-md border border-linea bg-superficie p-4 text-sm text-texto">
      <h2 id={titleId} className="m-0 text-base font-semibold">
        {sheet.title}
      </h2>
      <dl className="m-0 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1">
        {sheet.fields.map((field) => (
          <div key={field.label} className="contents">
            <dt className="text-texto-suave">{field.label}</dt>
            <dd className="m-0">{field.value}</dd>
          </div>
        ))}
      </dl>
      <ol aria-label="Paradas" className="m-0 flex list-none flex-col gap-2 p-0">
        {sheet.stops.map((stop) => (
          <li key={stop.key} className="flex flex-col gap-0.5">
            <span className="tabular font-semibold">{stopHeading(stop)}</span>
            {stop.passengers.length > 0 && (
              <ul aria-label={`Pasajeros: ${stop.name}`} className="m-0 list-disc pl-5">
                {stop.passengers.map((passenger, i) => (
                  <li key={i}>{passengerText(passenger)}</li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ol>
      <div className="hoja-sin-imprimir flex flex-col gap-2">
        {sheet.personal && (
          <p className="m-0 text-xs text-texto-suave">
            La hoja tiene nombres y domicilios de pasajeros. Compartila solo con el conductor del viaje.
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secundario"
            onClick={() => {
              if (ref.current !== null) browser.print(ref.current);
            }}
          >
            Imprimir
          </Button>
          <Button variant="secundario" onClick={() => copy(text, "copied")}>
            Copiar texto
          </Button>
          <Button variant="secundario" onClick={() => copy(dispatchText(sheet), "dispatch_copied")}>
            Copiar datos del viaje
          </Button>
          {share !== undefined && (
            <Button
              onClick={() => {
                share({ title: sheet.title, text }).then(
                  () => setStatus("shared"),
                  // Cancelar la hoja de compartir del sistema tambien rechaza: no es un error.
                  () => setStatus("idle"),
                );
              }}
            >
              Compartir
            </Button>
          )}
        </div>
        {status !== "idle" && <Alert variant={SHARE_COPY[status].variant}>{SHARE_COPY[status].text}</Alert>}
      </div>
    </section>
  );
}

type DetailState = { estado: "cargando" } | { estado: "error" } | { estado: "listo"; data: ProviderTripDetailResponse };

export interface TripRouteSheetProps {
  session: PortalSession;
  port: Pick<RequestsPort, "detail">;
  /** La habilitacion elegida: su nombre va en la hoja, su id solo en la llamada. */
  site: RequestsSite & { readonly name: string };
  trip: ProviderTrip;
  vehicles: RosterState<SiteVehicle>;
  drivers: RosterState<Driver>;
  actions?: RouteSheetActions;
}

/**
 * Pide el detalle (lectura auditada por la API) y arma la hoja. El vehiculo y
 * el conductor salen de la nomina que el portal ya tiene cargada; si no estan,
 * la hoja dice "Sin asignar" y nunca muestra el id local.
 */
export function TripRouteSheet({ session, port, site, trip, vehicles, drivers, actions }: TripRouteSheetProps): React.JSX.Element {
  const [state, setState] = useState<DetailState>({ estado: "cargando" });
  const { siteId, providerId } = site;
  const tripId = trip.tripId;
  useEffect(() => {
    let current = true;
    setState({ estado: "cargando" });
    port
      .detail(session, { siteId, providerId }, tripId)
      .then((data) => {
        if (current) setState({ estado: "listo", data });
      })
      .catch(() => {
        if (current) setState({ estado: "error" });
      });
    return () => {
      current = false;
    };
  }, [port, session, siteId, providerId, tripId]);

  if (state.estado === "cargando") return <Alert variant="info">Consultando la hoja de ruta...</Alert>;
  if (state.estado === "error") return <Alert variant="error">No se pudo consultar la hoja de ruta. Reintentar en unos minutos.</Alert>;
  if (state.data.stops.length === 0) return <Alert variant="info">La hoja de ruta no está disponible para este viaje.</Alert>;
  const { vehicleId, driverId } = state.data.trip;
  const vehicle = vehicles.estado === "listo" && vehicleId !== undefined ? vehicles.items.find((v) => v.id === vehicleId) : undefined;
  const driver = drivers.estado === "listo" && driverId !== undefined ? drivers.items.find((d) => d.id === driverId) : undefined;
  const sheet = buildRouteSheet({
    detail: state.data,
    siteName: site.name,
    ...(vehicle !== undefined ? { vehicle: { code: vehicle.code, plate: vehicle.plate } } : {}),
    ...(driver !== undefined ? { driver: { code: driver.code, name: driver.name } } : {}),
  });
  return <RouteSheetView sheet={sheet} {...(actions !== undefined ? { actions } : {})} />;
}
